import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@cokitchen/cokitchen-components';
import { AddIcon, AddressIcon, RmxArrowBackIcon } from 'cokitchen-icons';
import LocalModal from 'components/base/local-modal';
import useOrdersContext from 'contexts/orders.context';
import { Bars, ThreeDots } from 'react-loader-spinner';
import bulkOrderService from 'services/bulk-order.service';
import useDashboardContext from 'contexts/dashboard.context';
import { SwipeableList, SwipeableListItem, Type as ListType } from 'react-swipeable-list';

import AddressItem from 'components/address-item';
import DeleteAddress from 'components/delete-address';
import groupOrderService from 'services/group-order.service';
import AddressInput from 'components/base/address-input';
import BranchAddressItem from 'components/branch-address-item';
import { LocationObjectType } from 'models/location.model';

export type AddressType = {
  name: string;
  address_line: string;
  city: string;
  phone_number: string;
  building_number: string;
};
type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (address: LocationObjectType) => void;
  defaultSelectedAddress?:
    | (AddressType & {
        phone_number: string;
      })
    | null;
  loading?: boolean;
};
const AddressModal = (props: Props) => {
  const { savedAddressList, addressListLoading, branchAddressesLoading, branchAddresses } =
    useDashboardContext();
  const {
    calculatedOrderForm,
    currentAddress,
    saveAddressloading,
    currentAddressLoading,
    handleSaveAddress,
    setCoordinates,
    locationAccess
  } = useOrdersContext();
  const { open, onClose, onConfirm, defaultSelectedAddress, loading: confirmLoading } = props;
  const [view, setView] = useState<'select' | 'new'>('select');
  const [formattedCurrentAddress, setFormattedCurrentAddress] = useState<LocationObjectType>();
  const [value, setValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<LocationObjectType>();
  const [sortedAddresses, setSortedAddress] = useState<LocationObjectType[]>([]);

  const addNewAddress = async () => {
    await handleSaveAddress(value, 'Other');
    setView('select');
    setValue('');
  };

  useEffect(() => {
    if (savedAddressList.length > 0 && formattedCurrentAddress) {
      if (defaultSelectedAddress) {
        const selected = savedAddressList.find(
          (address) => address.address === defaultSelectedAddress.address_line
        );
        if (selected) {
          setSelectedAddress(selected);
        } else {
          setSelectedAddress(formattedCurrentAddress);
        }
      } else {
        setSelectedAddress(formattedCurrentAddress);
      }
    }
  }, [savedAddressList, formattedCurrentAddress, defaultSelectedAddress]);

  useMemo(() => {
    if (savedAddressList.length > 0) {
      setSortedAddress(groupOrderService.RearrangeLocations(savedAddressList));
    }
  }, [savedAddressList]);
  useEffect(() => {
    if (currentAddress !== '') {
      const getFormattedAddress = async () => {
        const formattedAddress = await bulkOrderService.FormatAddress(currentAddress, 'Default');
        setFormattedCurrentAddress(formattedAddress);
      };
      getFormattedAddress();
    }
  }, [currentAddress]);

  return (
    <LocalModal isOpen={open} onRequestClose={onClose} title='Set delivery location'>
      <div className='min-h-[285px] flex flex-col justify-between'>
        {view === 'select' ? (
          <>
            <div className='flex flex-col'>
              <div
                className='w-full flex items-center gap-3 pb-[18px] text-[#0080E5] border-b-[0.8px] border-b-[#E7E8E9] cursor-pointer'
                onClick={() => {
                  setView('new');
                }}>
                <AddIcon className='w-[18px] h-[18px]' />
                <p className='text-sm font-medium'>New address</p>
              </div>
              {locationAccess && (
                <div
                  className='w-full flex items-center justify-between gap-3 py-[18px] border-b-[0.8px] border-b-[#E7E8E9] cursor-pointer'
                  onClick={() => setSelectedAddress(formattedCurrentAddress)}>
                  <div className='flex gap-3 items-center w-[90%]'>
                    <AddressIcon />
                    <div className='w-[65%]'>
                      <p className='text-[#2E3031] text-sm font-medium capitalize'>
                        Current Location
                      </p>
                      {currentAddressLoading ? (
                        <ThreeDots
                          visible={true}
                          height='20'
                          width='50'
                          color='#E60A2B'
                          radius='9'
                          ariaLabel='three-dots-loading'
                        />
                      ) : (
                        <p className='text-[#5E6164] text-sm line-clamp-1'>
                          {`
                     ${
                       formattedCurrentAddress?.street_number !== null
                         ? `${formattedCurrentAddress?.street_number}, `
                         : ''
                     }${formattedCurrentAddress?.address}
                      `}
                        </p>
                      )}
                    </div>
                  </div>
                  <span
                    className={`ml-auto w-[14px] h-[14px] rounded-full ${
                      selectedAddress === formattedCurrentAddress
                        ? 'border-[3.8px] border-[#E60A2B]'
                        : 'border-[1.3px] border-[#D6D8E0]'
                    }`}
                  />
                </div>
              )}
              <div className='w-full'>
                {addressListLoading || branchAddressesLoading ? (
                  <div className='w-full h-[20vh] flex justify-center items-center'>
                    <Bars color='#E60A2B' height='50' width='50' />
                  </div>
                ) : (
                  <div className='w-full min-h-[5vh] max-h-[30vh] overflow-y-scroll no-scrollbar'>
                    <SwipeableList type={ListType.IOS} threshold={0.5}>
                      {branchAddresses.map((address, i) => {
                        return (
                          <SwipeableListItem
                            key={i}
                            scrollStartThreshold={0.5}
                            trailingActions={
                              <DeleteAddress
                                id={address.id}
                                loading={loading}
                                setLoading={setLoading}
                                isBranchAddress
                              />
                            }>
                            <BranchAddressItem
                              address={address}
                              isSelected={selectedAddress?.id === address.id}
                              setSelectedAddress={setSelectedAddress}
                              number={i + 1}
                            />
                          </SwipeableListItem>
                        );
                      })}
                      {sortedAddresses.map((address, i) => {
                        return (
                          <SwipeableListItem
                            key={i}
                            scrollStartThreshold={0.5}
                            trailingActions={
                              <DeleteAddress
                                id={address?.id ?? ''}
                                loading={loading}
                                setLoading={setLoading}
                              />
                            }>
                            {' '}
                            <AddressItem
                              address={address}
                              selectedAddress={selectedAddress}
                              setSelectedAddress={setSelectedAddress}
                            />
                          </SwipeableListItem>
                        );
                      })}
                    </SwipeableList>
                  </div>
                )}
              </div>
            </div>
            <div className='flex items-end justify-end gap-2 pt-4'>
              <Button
                variant='block'
                color='primary'
                className='h-[41px] w-[139px]'
                loading={confirmLoading}
                onClick={() => {
                  if (selectedAddress) {
                    onConfirm(selectedAddress);
                    setCoordinates({
                      lat: selectedAddress.lat.toString(),
                      lng: selectedAddress.lng.toString()
                    });
                  }
                }}
                disabled={!calculatedOrderForm.address_details}>
                Confirm
              </Button>
            </div>
          </>
        ) : (
          <div>
            <div className='flex flex-col gap-3'>
              <button
                className='self-start p-[5px] bg-[#F4F1F1] rounded-full'
                onClick={() => {
                  setView('select');
                }}>
                <RmxArrowBackIcon className='[&>path]:fill-[#E60A2B]' />
              </button>
              <AddressInput
                label='Provide address'
                placeholder='Enter address'
                value={value}
                onChange={setValue}
              />
            </div>
            <div className='flex items-end justify-end gap-2 pt-4'>
              <Button
                variant='block'
                color='primary'
                className='h-[41px] w-[139px]'
                onClick={addNewAddress}
                loading={saveAddressloading}
                disabled={value === ''}>
                Confirm
              </Button>
            </div>
          </div>
        )}
      </div>
    </LocalModal>
  );
};

export default AddressModal;
